import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Star, Eye, EyeOff, Upload, Undo2 } from "lucide-react";
import { adminApi } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" }) : "—");
const detail = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong."; };

export const AnnounceCard = ({ siteMode }) => {
  const [info, setInfo] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await adminApi.announceInfo();
      setInfo(d);
      setSubject((s) => s || d.defaults.subject);
      setBody((b) => b || d.defaults.body);
    } catch (err) { toast.error(detail(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setBusy("test");
    try { const r = await adminApi.announceTest({ subject, body }); toast.success(r.ok ? `Test email sent to ${r.to}` : `Could not send to ${r.to}. Check the Resend setup.`); }
    catch (err) { toast.error(detail(err)); }
    finally { setBusy(""); }
  };
  const sendAll = async () => {
    setConfirm(false); setBusy("send");
    try { const r = await adminApi.announceSend({ subject, body }); setResult(r); setInfo((i) => ({ ...i, ...r })); toast.success(`Announcement sent to ${r.sent} ${r.sent === 1 ? "person" : "people"}.`); }
    catch (err) { toast.error(detail(err)); }
    finally { setBusy(""); }
  };
  if (!info) return null;
  const valid = subject.trim().length >= 3 && body.trim().length >= 10;

  return (
    <div className="launch-card announce-card" data-testid="announce-card">
      <h3><Megaphone size={18} /> Announce the launch</h3>
      <p className="cms-hint">Email everyone who signed up on the Coming Soon page. Each person is emailed once; sending again only reaches new sign-ups. A "Get the eBook" button is added automatically.</p>
      <div className="announce-stats">
        <div data-testid="announce-pending"><strong>{info.pending}</strong><span>waiting to hear</span></div>
        <div data-testid="announce-total"><strong>{info.total}</strong><span>on the launch list</span></div>
        <div data-testid="announce-last"><strong>{info.lastSentAt ? fmt(info.lastSentAt) : "Never"}</strong><span>{info.lastSentAt ? `last sent to ${info.lastSentCount}` : "not sent yet"}</span></div>
      </div>
      {siteMode !== "live" && info.pending > 0 && <div className="announce-note" data-testid="announce-not-live">The site is still in Coming Soon mode. Flip it to Live above before announcing, so the "Get the eBook" button works for readers.</div>}
      <label className="cms-field"><span>Subject</span><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="announce-subject" /></label>
      <label className="cms-field"><span>Message</span><textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} data-testid="announce-body" /></label>
      <div className="launch-link-actions">
        <button type="button" className="admin-ghost admin-ghost-dark" onClick={sendTest} disabled={!valid || !!busy} data-testid="announce-test-btn"><Send size={14} /> {busy === "test" ? "Sending..." : "Send me a test first"}</button>
        {!confirm ? (
          <button type="button" className="btn btn-gold" onClick={() => setConfirm(true)} disabled={!valid || !!busy || info.pending === 0} data-testid="announce-send-btn"><Megaphone size={14} /> {busy === "send" ? "Sending..." : `Send to ${info.pending} ${info.pending === 1 ? "person" : "people"}`}</button>
        ) : (
          <span className="announce-confirm" data-testid="announce-confirm">
            <span>Send this email to {info.pending} {info.pending === 1 ? "person" : "people"} now?</span>
            <button type="button" className="btn btn-gold" onClick={sendAll} data-testid="announce-confirm-yes">Yes, send it</button>
            <button type="button" className="admin-link admin-link-muted" onClick={() => setConfirm(false)} data-testid="announce-confirm-no">Cancel</button>
          </span>
        )}
      </div>
      {result && <div className="announce-result" data-testid="announce-result">Sent: <strong>{result.sent}</strong>{result.failed ? <> · Failed: <strong>{result.failed}</strong> (they stay in the queue for the next send)</> : ""}</div>}
    </div>
  );
};

const StarRow = ({ n }) => <span className="rv-admin-stars" aria-label={`${n} out of 5`}>{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} fill={i <= n ? "currentColor" : "none"} className={i <= n ? "on" : ""} />)}</span>;

export const ReviewsView = ({ onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await adminApi.reviews()); }
    catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const patch = (updated) => setRows((r) => r.map((x) => (x.id === updated.id ? updated : x)));
  const setStatus = async (r, status) => {
    try { patch(await adminApi.updateReview(r.id, status)); toast.success(status === "hidden" ? "Review hidden" : status === "approved" ? "Review approved" : "Review reopened"); }
    catch (err) { toast.error(detail(err)); }
  };
  const publish = async (r) => {
    try { const res = await adminApi.publishReview(r.id); patch({ ...r, published: true, status: "approved" }); toast.success(`Published. The site now shows ${res.testimonialsCount} testimonial${res.testimonialsCount === 1 ? "" : "s"}.`); }
    catch (err) { toast.error(detail(err)); }
  };
  const unpublish = async (r) => {
    try { await adminApi.unpublishReview(r.id); patch({ ...r, published: false }); toast.success("Removed from the website"); }
    catch (err) { toast.error(detail(err)); }
  };
  const runNow = async () => {
    try { const res = await adminApi.runReviewRequests(); toast.success(res.sent ? `Review requests sent to ${res.sent} buyer${res.sent === 1 ? "" : "s"}.` : "No buyers are due for a review request right now."); }
    catch (err) { toast.error(detail(err)); }
  };

  const visible = rows.filter((r) => filter === "all" || (filter === "published" ? r.published : r.status === filter));
  const avg = rows.length ? (rows.reduce((s, r) => s + r.rating, 0) / rows.length).toFixed(1) : "–";

  return (
    <div data-testid="admin-reviews">
      <div className="admin-title"><span className="eyebrow">Reader feedback, requested 7 days after purchase</span><h1>Reviews</h1></div>
      <div className="admin-stats">
        <div className="admin-stat" data-testid="reviews-stat-total"><span>Reviews received</span><strong>{rows.length}</strong></div>
        <div className="admin-stat" data-testid="reviews-stat-new"><span>Awaiting your decision</span><strong>{rows.filter((r) => r.status === "new").length}</strong></div>
        <div className="admin-stat" data-testid="reviews-stat-published"><span>Live on the website</span><strong>{rows.filter((r) => r.published).length}</strong></div>
        <div className="admin-stat" data-testid="reviews-stat-avg"><span>Average rating</span><strong>{avg}</strong></div>
      </div>
      <div className="admin-toolbar">
        <div className="admin-filters">
          {[["all", "All"], ["new", "New"], ["approved", "Approved"], ["published", "Published"], ["hidden", "Hidden"]].map(([k, l]) => <button key={k} className={`filter-btn${filter === k ? " active" : ""}`} onClick={() => setFilter(k)} data-testid={`reviews-filter-${k}`}>{l}</button>)}
        </div>
        <div className="admin-header-actions">
          <button className="admin-ghost admin-ghost-dark" onClick={runNow} data-testid="reviews-run-btn">Send due review requests now</button>
          <button className="admin-ghost admin-ghost-dark" onClick={load} data-testid="reviews-refresh-btn">Refresh</button>
        </div>
      </div>
      <div className="admin-table-wrap">
        {loading && !rows.length ? <div className="admin-empty">Loading reviews...</div>
          : !visible.length ? <div className="admin-empty" data-testid="reviews-empty">No reviews here yet. Buyers receive a review request one week after purchase; you can also send one from the Purchases tab.</div> : (
          <table className="admin-table" data-testid="reviews-table">
            <thead><tr><th>Received</th><th>Rating</th><th>Review</th><th>Reader</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} data-testid={`review-row-${r.id}`}>
                  <td className="admin-muted">{fmt(r.createdAt)}</td>
                  <td><StarRow n={r.rating} /></td>
                  <td className="admin-topic rv-admin-quote">“{r.quote}”</td>
                  <td><strong>{r.name}</strong><br /><span className="admin-muted">{r.role || "No role given"}</span><br /><a href={`mailto:${r.email}`} className="admin-muted">{r.email}</a></td>
                  <td>
                    {r.published ? <span className="admin-pill" style={{ background: "rgba(47,118,94,.14)", color: "#2F765E" }}>On the website</span>
                      : r.status === "hidden" ? <span className="admin-pill" style={{ background: "rgba(13,42,36,.08)", color: "var(--muted)" }}>Hidden</span>
                      : r.status === "approved" ? <span className="admin-pill">Approved</span>
                      : <span className="admin-pill" style={{ background: "rgba(180,70,47,.12)", color: "#b4462f" }}>New</span>}
                  </td>
                  <td className="rv-admin-actions">
                    {!r.published && <button className="admin-link" onClick={() => publish(r)} data-testid={`review-publish-${r.id}`}><Upload size={13} /> Publish as testimonial</button>}
                    {r.published && <button className="admin-link admin-link-muted" onClick={() => unpublish(r)} data-testid={`review-unpublish-${r.id}`}><Undo2 size={13} /> Remove from website</button>}
                    {!r.published && r.status !== "approved" && <button className="admin-link" onClick={() => setStatus(r, "approved")} data-testid={`review-approve-${r.id}`}><Eye size={13} /> Approve</button>}
                    {!r.published && r.status !== "hidden" && <button className="admin-link admin-link-muted" onClick={() => setStatus(r, "hidden")} data-testid={`review-hide-${r.id}`}><EyeOff size={13} /> Hide</button>}
                    {!r.published && r.status === "hidden" && <button className="admin-link admin-link-muted" onClick={() => setStatus(r, "new")} data-testid={`review-reopen-${r.id}`}>Reopen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
